import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { type WireMoney } from '@vello/api-client';
import { Button, MoneyInput, Notice, Row, FormStack } from '@vello/web-theme';
import { cashSettleDraft, wireMinor } from '../../src/index';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { FormDialog } from './form-dialog';

const AMOUNT_ID = 'ride-cash-amount';

export interface RideCashSettleDialogProps {
  readonly open: boolean;
  readonly rideCode: string;
  /** What the ride charged (the final fare, else the quote): the default value and the upper bound. */
  readonly due: WireMoney;
  readonly busy: boolean;
  /** The translated refusal of the last attempt; the dialog stays open to correct or retry. */
  readonly error: string | undefined;
  /** Renders an amount in the ride's market convention. */
  readonly money: (m: WireMoney) => string;
  readonly onCancel: () => void;
  readonly onConfirm: (amount: WireMoney) => void;
}

/**
 * "Registrar recebimento" (W8-FINAL-FARE-AND-CASH, D438; finding A-03) — the cash a driver handed in for a completed ride,
 * in two steps inside one in-app dialog: the amount (the design-system `MoneyInput`, in the ride's currency, prefilled
 * with what the ride charged and never above it), then a REVIEW that restates the amount and the ride before the one
 * confirm button posts. Refusals come back translated and keep the dialog open.
 */
export function RideCashSettleDialog({ open, rideCode, due, busy, error, money, onCancel, onConfirm }: RideCashSettleDialogProps): ReactElement | null {
  const { t, lang } = useT();
  const { v } = useVocab();
  const [amount, setAmount] = useState<bigint | undefined>(undefined);
  const [step, setStep] = useState<'amount' | 'review'>('amount');

  // A fresh draft per opening: the due, on the amount step.
  useEffect(() => {
    if (!open) return;
    setAmount(wireMinor(due));
    setStep('amount');
  }, [open, due]);

  // A refusal sends the operator back to the amount (it is what most refusals are about).
  useEffect(() => {
    if (error !== undefined) setStep('amount');
  }, [error]);

  const draft = cashSettleDraft(amount, due);
  const issue = !draft.ok && amount !== undefined ? draft.issue : undefined;

  const submit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (busy || !draft.ok) return;
    if (step === 'amount') setStep('review');
    else onConfirm(draft.amount);
  };

  return (
    <FormDialog
      open={open}
      id="ride-cash-settle"
      icon="badgeCheck"
      title={t('rideCash.settle.title', { code: rideCode })}
      body={t('rideCash.settle.body')}
      busy={busy}
      initialFocusId={AMOUNT_ID}
      onCancel={onCancel}
    >
      <FormStack gap={4} onSubmit={submit} noValidate>
        {step === 'amount' ? (
          <>
            <MoneyInput
              id={AMOUNT_ID}
              label={t('rideCash.settle.amount')}
              currency={due.currency}
              locale={lang}
              valueMinor={amount}
              onChangeMinor={setAmount}
              issueText={(i) => v('format.money_input_issue', i)}
              hint={t('rideCash.settle.amount.hint', { due: money(due) })}
              required
              disabled={busy}
            />
            {issue !== undefined && issue !== 'AMOUNT_REQUIRED' && (
              <div>
                <Notice tone="warning">{v('ride.cash_settle_form_issue', issue, { due: money(due) })}</Notice>
              </div>
            )}
          </>
        ) : (
          draft.ok && (
            <div>
              <Notice tone="neutral">
                <span>{t('rideCash.settle.summary', { amount: money(draft.amount), code: rideCode })}</span>
              </Notice>
            </div>
          )
        )}
        {error !== undefined && (
          <div>
            <Notice tone="danger">{error}</Notice>
          </div>
        )}
        <Row gap={2} align="stretch">
          {step === 'amount' ? (
            <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={onCancel} disabled={busy}>{t('ui.cancel')}</Button>
          ) : (
            <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={() => setStep('amount')} disabled={busy}>{t('rideCash.settle.back')}</Button>
          )}
          <Button className="vello-admin-a-btn-grow" type="submit" variant="primary" disabled={busy || !draft.ok}>
            {step === 'amount' ? t('rideCash.settle.review') : busy ? t('rideCash.settle.sending') : t('rideCash.settle.confirm')}
          </Button>
        </Row>
      </FormStack>
    </FormDialog>
  );
}
